import type { ErrorCode } from './protocol.ts'
import {
  ACTION_CARD_CREATE,
  ACTION_LIST,
  ACTION_SELECT,
  type CardDraft,
  type PaymentMethodSummary,
  type SessionConfig,
  type SuccessResult,
  type TerminalError
} from './types.ts'

export type Status =
  | 'boot'
  | 'ready_posted'
  | 'exchanging'
  | 'method_list'
  | 'card_entry'
  | 'submitting'
  | 'success'
  | 'error'
  | 'closed'

export interface TerminalState {
  status: Status
  generation: number
  session: SessionConfig | null
  methods: PaymentMethodSummary[]
  draft: CardDraft | null
  result: SuccessResult | null
  error: TerminalError | null
}

export type TerminalEvent =
  | { type: 'READY_POSTED' }
  | { type: 'INIT'; generation: number }
  | {
      type: 'SESSION_LOADED'
      generation: number
      session: SessionConfig
      methods: PaymentMethodSummary[]
    }
  | { type: 'OPEN_CARD_ENTRY' }
  | { type: 'CARD_TOKENIZED'; generation: number; draft: CardDraft }
  | { type: 'BACK' }
  | { type: 'METHOD_CREATED'; generation: number; method: PaymentMethodSummary }
  | { type: 'METHOD_SELECTED'; paymentMethodId: number }
  | { type: 'FAILED'; generation: number; code: ErrorCode; message: string }
  | { type: 'RETRY' }
  | { type: 'CLOSE' }

export const initialState: TerminalState = {
  status: 'boot',
  generation: 0,
  session: null,
  methods: [],
  draft: null,
  result: null,
  error: null
}

const RETRYABLE: ReadonlySet<ErrorCode> = new Set<ErrorCode>([
  'processor_decline',
  'duplicate_card',
  'validation',
  'rate_limited',
  'unavailable'
])

function allows(state: TerminalState, action: string): boolean {
  return state.session?.actions.includes(action) ?? false
}

function result(
  state: TerminalState,
  method: PaymentMethodSummary,
  action: SuccessResult['action']
): SuccessResult {
  return {
    action,
    actionId: state.session?.actionId ?? '',
    paymentMethodId: method.paymentMethodId,
    paymentType: method.paymentType,
    brand: method.brand,
    last4: method.last4,
    expMonth: method.expMonth,
    expYear: method.expYear,
    status: action === ACTION_SELECT ? 'selected' : 'created'
  }
}

function afterSession(state: TerminalState): TerminalState {
  if (allows(state, ACTION_LIST)) return { ...state, status: 'method_list' }
  if (allows(state, ACTION_CARD_CREATE)) {
    return { ...state, status: 'card_entry' }
  }
  return {
    ...state,
    status: 'error',
    error: {
      code: 'action_forbidden',
      message: 'This session allows no action this terminal supports.'
    }
  }
}

/**
 * Applies one event to the terminal state, dropping events from an earlier generation.
 * @param state - The current state.
 * @param event - The event.
 * @returns The next state, or the same object when the event does not apply.
 */
export function reduce(
  state: TerminalState,
  event: TerminalEvent
): TerminalState {
  if ('generation' in event && event.type !== 'INIT') {
    if (event.generation !== state.generation) return state
  }
  if (state.status === 'closed' && event.type !== 'INIT') return state

  switch (event.type) {
    case 'READY_POSTED':
      return state.status === 'boot'
        ? { ...state, status: 'ready_posted' }
        : state
    case 'INIT':
      if (event.generation <= state.generation) return state
      return {
        ...initialState,
        status: 'exchanging',
        generation: event.generation
      }
    case 'SESSION_LOADED':
      if (state.status !== 'exchanging') return state
      return afterSession({
        ...state,
        session: event.session,
        methods: event.methods
      })
    case 'OPEN_CARD_ENTRY':
      if (state.status !== 'method_list') return state
      if (!allows(state, ACTION_CARD_CREATE)) return state
      return { ...state, status: 'card_entry' }
    case 'CARD_TOKENIZED':
      if (state.status !== 'card_entry') return state
      return { ...state, status: 'submitting', draft: event.draft }
    case 'BACK':
      if (state.status === 'card_entry' && allows(state, ACTION_LIST)) {
        return { ...state, status: 'method_list' }
      }
      if (
        state.status === 'error' &&
        state.error?.code === 'duplicate_card' &&
        allows(state, ACTION_LIST)
      ) {
        return { ...state, status: 'method_list', error: null, draft: null }
      }
      return state
    case 'METHOD_CREATED':
      if (state.status !== 'submitting') return state
      return {
        ...state,
        status: 'success',
        draft: null,
        methods: [...state.methods, event.method],
        result: result(state, event.method, ACTION_CARD_CREATE)
      }
    case 'METHOD_SELECTED': {
      if (state.status !== 'method_list') return state
      const method = state.methods.find(
        m => m.paymentMethodId === event.paymentMethodId
      )
      if (!method) return state
      return {
        ...state,
        status: 'success',
        result: result(state, method, ACTION_SELECT)
      }
    }
    case 'FAILED':
      return {
        ...state,
        status: 'error',
        error: { code: event.code, message: event.message }
      }
    case 'RETRY':
      if (state.status !== 'error' || !state.error) return state
      if (!RETRYABLE.has(state.error.code)) return state
      if (!allows(state, ACTION_CARD_CREATE)) return state
      return { ...state, status: 'card_entry', error: null, draft: null }
    case 'CLOSE':
      return { ...state, status: 'closed' }
    default:
      return state
  }
}
